import { useRef, useState } from "react";
import { Check, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import { buildIntakePayload, isServerSaveAvailable, saveIntakeToServer } from "@/lib/backendClient";

const ERROR_COPY = {
  pii_guardrail: {
    title: "Save blocked by the server privacy guardrail",
    description:
      "The server detected wording that may identify a participant or contain clinical detail. Your work is still saved locally in this browser — review free-text entries (such as the organisation name) and try again."
  },
  network: {
    title: "Server unavailable",
    description: "The snapshot could not reach the server. Your work remains saved locally in this browser."
  },
  validation: {
    title: "The server could not accept this snapshot",
    description: "Your work remains saved locally in this browser."
  },
  server: {
    title: "The server could not save this snapshot",
    description: "Your work remains saved locally in this browser."
  }
};

export function SaveToServerButton() {
  const { answers, result, serverIntakeId, markServerSaved } = useAssessment();
  const [saving, setSaving] = useState(false);
  const lastClickRef = useRef(0);

  if (!isServerSaveAvailable() || !result) return null;

  const handleSave = async () => {
    const now = Date.now();
    if (saving || now - lastClickRef.current < 300) return;
    lastClickRef.current = now;
    const payload = buildIntakePayload(answers, result);
    if (!payload.providerName) {
      toast.info("Add the provider organisation name in the intake before saving to the server.");
      return;
    }
    setSaving(true);
    const outcome = await saveIntakeToServer(payload);
    setSaving(false);
    if (outcome.ok) {
      markServerSaved(outcome.intake.id);
      toast.success("Snapshot saved to server", {
        description: `Server reference ${String(outcome.intake.id).slice(0, 8)}`
      });
      return;
    }
    const copy = ERROR_COPY[outcome.kind] || ERROR_COPY.server;
    toast.error(copy.title, { description: copy.description });
  };

  const showSavedState = Boolean(serverIntakeId) && !saving;
  let label = "Save to server";
  if (saving) label = "Saving\u2026";
  else if (serverIntakeId) label = "Saved to server";

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleSave}
      disabled={saving}
      data-testid="save-to-server-btn"
      className="rounded-sm border-border bg-card px-3 text-[13px] font-medium hover:bg-secondary"
    >
      {showSavedState ? <Check className="h-4 w-4" /> : <UploadCloud className="h-4 w-4" />}
      {label}
    </Button>
  );
}
